/**
 * webm-encoder.js — WebCodecs WebM (VP9 + Opus) transcoder.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Our FFmpeg-WASM core's libvpx encoder is broken: VP9 hits
 * "RuntimeError: memory access out of bounds" on ANY real decoded content
 * at every resolution and every arg combo (CQ / target-rate / threads=1 /
 * no-mt — all crash; proven 2026-07-02 on a fresh raw core, so it is the
 * core, not our wrappers). VP8 is inconsistent (works on some content,
 * OOBs on other). So every user-facing "export as WebM" that routed
 * through FFmpeg libvpx was broken in production. See
 * [[project_webm_platform_webcodecs]].
 *
 * The browser's NATIVE WebCodecs VideoEncoder does VP9 fine — measured
 * 640x480 in 85 ms, 1280x720 in 164 ms, with an Opus audio track, on the
 * exact content the WASM path died on. This module is the shared fix:
 * transcode any browser-decodable H.264/MP4 (a tool's WORKING FFmpeg
 * output) into real VP9/WebM.
 *
 * The single robust entry point for tools is `fromVideoBlob` — feed it the
 * MP4 your existing pipeline already produces; get back a WebM Blob.
 *
 *   const webm = await WebMEncoder.fromVideoBlob(mp4Blob, {
 *       crf: 24,                     // 18 hi … 38 lo (VP9 scale, mapped to bitrate)
 *       onProgress: p => {},         // 0..1
 *       signal: abortController.signal,
 *   });
 *   // → Blob(type video/webm) with VP9 video + Opus audio (if source had audio)
 *
 * `isSupported()` gates callers so a browser without WebCodecs/VP9/Opus
 * (Playwright WebKit, old Safari) falls back cleanly to the FFmpeg path
 * the caller already has.
 *
 * Dependencies (all vendored, loaded on demand):
 *   - window.WebCodecsPipeline._demuxMp4   (mp4box demux — battle-tested)
 *   - window.WebMMuxer                     (/vendor/webm-muxer/webm-muxer.min.js)
 *   - WebCodecs VideoDecoder/VideoEncoder/AudioEncoder — native
 *
 * ALPHA IS OUT OF SCOPE: WebCodecs cannot encode alpha VP8/VP9
 * (isConfigSupported({alpha:'keep'}) === false in Chromium). Alpha-WebM
 * tools (video-bg-remover, animated-avatar, circle-crop) need a separate
 * solution and must NOT call this module for their alpha output.
 */
(function () {
    'use strict';
    if (window.WebMEncoder) return;

    var MUXER_SRC = '/vendor/webm-muxer/webm-muxer.min.js';
    var VP9_PROFILES = ['vp09.00.10.08', 'vp09.00.30.08', 'vp09.00.20.08'];

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = src; s.onload = resolve;
            s.onerror = function () { reject(new Error('load: ' + src)); };
            document.head.appendChild(s);
        });
    }
    async function ensureMuxer() {
        if (window.WebMMuxer) return;
        if (window.__webmMuxerLoading) { await window.__webmMuxerLoading; return; }
        window.__webmMuxerLoading = loadScript(MUXER_SRC)
            .catch(function () { return loadScript('https://cdn.jsdelivr.net/npm/webm-muxer@5.1.4/build/webm-muxer.min.js'); });
        await window.__webmMuxerLoading;
    }
    // WebCodecsPipeline._demuxMp4 assumes mp4box is already loaded (run() loads
    // it upstream). We call the demuxer directly, so ensure it ourselves —
    // same source + jsdelivr fallback the pipeline uses.
    async function ensureMp4Box() {
        if (window.MP4Box) return;
        if (window.__mp4boxLoadingPromise) { await window.__mp4boxLoadingPromise; return; }
        window.__mp4boxLoadingPromise = loadScript('/vendor/mp4box/mp4box.all.min.js')
            .catch(function () { return loadScript('https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'); });
        await window.__mp4boxLoadingPromise;
    }

    // VP9 VBR bitrate from a VP9-scale crf (18 hi … 38 lo). bpp interpolated
    // 0.14 (crf18) → 0.03 (crf38); floored 500 kbps, capped 40 Mbps. VP9 is
    // ~35 % more efficient than x264 at equal quality, so these run leaner
    // than the H.264 table.
    function bitrateFor(w, h, fps, crf) {
        var c = parseInt(crf, 10); if (isNaN(c)) c = 24;
        c = Math.max(18, Math.min(38, c));
        var bpp = 0.14 - (c - 18) * (0.11 / 20);
        return Math.round(Math.max(500000, Math.min(40000000, w * h * (fps || 30) * bpp)));
    }

    async function pickVp9(w, h, bitrate, fps) {
        if (typeof VideoEncoder === 'undefined' || !VideoEncoder.isConfigSupported) return null;
        for (var i = 0; i < VP9_PROFILES.length; i++) {
            for (var hw = 0; hw < 2; hw++) {
                try {
                    var cfg = { codec: VP9_PROFILES[i], width: w, height: h, bitrate: bitrate, framerate: fps };
                    cfg.hardwareAcceleration = hw === 0 ? 'prefer-hardware' : 'no-preference';
                    var r = await VideoEncoder.isConfigSupported(cfg);
                    if (r && r.supported) return { codec: VP9_PROFILES[i], config: r.config || cfg, hardware: hw === 0 };
                } catch (_) {}
            }
        }
        return null;
    }

    function samples0Dur(d) {
        var s = d && d.samples;
        if (!s || !s.length) return 0;
        var last = s[s.length - 1];
        return (last.timestamp || 0) + (last.duration || 0) - (s[0].timestamp || 0);
    }

    async function isSupported(opts) {
        opts = opts || {};
        var out = { ok: false, reason: '' };
        if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined' ||
            typeof VideoFrame === 'undefined' || typeof EncodedVideoChunk === 'undefined') {
            out.reason = 'no-webcodecs'; return out;
        }
        if (!window.WebCodecsPipeline || typeof window.WebCodecsPipeline._demuxMp4 !== 'function') {
            out.reason = 'no-demuxer'; return out;
        }
        try { await Promise.all([ensureMuxer(), ensureMp4Box()]); } catch (_) { out.reason = 'no-muxer'; return out; }
        if (!window.WebMMuxer) { out.reason = 'no-muxer'; return out; }
        if (!window.MP4Box) { out.reason = 'no-mp4box'; return out; }
        var vc = await pickVp9(opts.width || 1280, opts.height || 720, 3000000, opts.fps || 30);
        if (!vc) { out.reason = 'no-vp9-encoder'; return out; }
        out.ok = true; out.videoCodec = vc.codec; out.hardware = vc.hardware;
        return out;
    }

    // The whole track decoded to PCM — for what the relay below cannot carry.
    // WebCodecsAudioDecode first (it reads the rate off the container's clock;
    // native decodeAudioData hands Yandex HE-AAC v2 back twice as long, an
    // octave lower — docs/why/primitives/audio-decode-robust.md), native as
    // the fallback. A rate Opus will not take is rendered to 48 kHz HERE, so
    // the muxer track is planned from what will really be encoded.
    async function decodeWholeAudio(file, isAborted) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC || typeof AudioData === 'undefined' || typeof AudioEncoder === 'undefined') {
            throw new Error('no-audio-encoder');
        }
        var WC = window.WebCodecsAudioDecode, buf = null, ctx;
        if (WC && typeof WC.decode === 'function' && WC.supported(file)) {
            ctx = new AC();
            try { buf = await WC.decode(file, ctx); } catch (_) { buf = null; }
            finally { try { ctx.close(); } catch (_) {} }
        }
        if (!buf || !buf.length) {
            var bytes = await file.arrayBuffer();
            ctx = new AC();
            try { buf = await ctx.decodeAudioData(bytes.slice(0)); }
            finally { try { ctx.close(); } catch (_) {} }
        }
        if (isAborted && isAborted()) throw new DOMException('Aborted', 'AbortError');
        var ch = Math.min(2, buf.numberOfChannels), ok = false;
        try { var se = await AudioEncoder.isConfigSupported({ codec: 'opus', sampleRate: buf.sampleRate, numberOfChannels: ch, bitrate: 128000 }); ok = !!(se && se.supported); } catch (_) {}
        if (!ok || buf.numberOfChannels > 2) {
            var rate = ok ? buf.sampleRate : 48000;
            var oac = new OfflineAudioContext(ch, Math.max(1, Math.ceil(buf.duration * rate)), rate);
            var src = oac.createBufferSource(); src.buffer = buf; src.connect(oac.destination); src.start();
            buf = await oac.startRendering();
        }
        return buf;
    }

    // The decoded track to a WebM Opus track. Any failure throws → the
    // caller's fallback; better a retry than shipping a WebM with no sound.
    async function muxOpusAudio(buf, muxer, isAborted) {
        var sr = buf.sampleRate, ch = Math.min(2, buf.numberOfChannels), total = buf.length;
        var encErr = null, first = true, muxed = 0;
        var aenc = new AudioEncoder({
            output: function (chunk, meta) {
                try { muxer.addAudioChunk(chunk, (first && meta) ? meta : undefined); muxed++; first = false; }
                catch (e) { encErr = encErr || e; }
            },
            error: function (e) { encErr = encErr || e; }
        });
        aenc.configure({ codec: 'opus', sampleRate: sr, numberOfChannels: ch, bitrate: 128000 });
        var ch0 = buf.getChannelData(0);
        var ch1 = ch > 1 ? buf.getChannelData(1) : ch0;
        var block = sr, tUs = 0;
        for (var off = 0; off < total; off += block) {
            if (encErr) throw encErr;
            if (isAborted && isAborted()) { try { aenc.close(); } catch (_) {} throw new DOMException('Aborted', 'AbortError'); }
            var n = Math.min(block, total - off);
            var inter = new Float32Array(n * ch);   // f32 interleaved (Opus in Chromium wants interleaved)
            for (var i = 0; i < n; i++) {
                inter[i * ch] = ch0[off + i];
                if (ch > 1) inter[i * ch + 1] = ch1[off + i];
            }
            var ad = new AudioData({ format: 'f32', sampleRate: sr, numberOfFrames: n, numberOfChannels: ch, timestamp: tUs, data: inter });
            aenc.encode(ad); ad.close();
            tUs += (n / sr) * 1e6;
        }
        await aenc.flush();
        try { aenc.close(); } catch (_) {}
        if (encErr) throw encErr;
        return muxed;
    }

    // The audio track as a RELAY — AAC access units off the disk in 8 MB
    // windows (the pipeline's lazy sample reader) → AudioDecoder → Opus
    // AudioEncoder → the muxer, nothing alive past its window. The whole-file
    // path above holds the PCM of the whole track: measured 2026-09-26 on a
    // 20-min 720p intermediate (350 MB), the transcode added 2.1 GB to the
    // browser.
    //
    // planRelay decides, BEFORE the muxer exists, whether the relay carries
    // this stream and in what format: what the decoder really emits
    // (WebCodecsAudioDecode.relayFormat — decoded units and the container's
    // clock), not the sample entry. Until 2026-10-08 the encoder and the track
    // were configured from the sample entry, and every HE-AAC v2 video (TikTok,
    // most phones) failed «Input audio buffer is incompatible with codec
    // parameters» in Chrome, Edge and Yandex — the decoder emits mono where the
    // header says stereo, Yandex labels the rate ×2. null → the whole path.
    async function planRelay(demux, rd) {
        var a = demux.audio, WAD = window.WebCodecsAudioDecode;
        if (!a || !a.samples || !a.samples.length || !rd) return null;
        if (typeof AudioDecoder === 'undefined' || typeof AudioEncoder === 'undefined' || typeof EncodedAudioChunk === 'undefined') return null;
        if (!WAD || typeof WAD.relayFormat !== 'function' || typeof WAD.conformAudioData !== 'function') return null;
        /* The source's edit list (applied by the lazy demux): the first kept
           access unit starts `audioResidualUs` BEFORE the picture — the AAC
           encoder priming an MP4 hides behind its elst (14.3 ms on an ffmpeg
           file, measured). decodeAudioData skipped it on its own; a relay has
           to cut it, or every WebM comes out that much late (measured: +15 ms
           against the old path before this cut). A real leading gap (audio
           joining later than the picture) stays on the whole-file path. */
        var ed = demux.edit || {};
        if (ed.applied && ed.audioLeadUs > (ed.audioUnitUs || 21333)) return null;
        var base = { codec: a.codec || 'mp4a.40.2', sampleRate: a.sampleRate || 48000, numberOfChannels: a.channels || 2 };
        if (a.ascBytes && a.ascBytes.length) base.description = a.ascBytes;
        var fmt = await WAD.relayFormat(base, a.samples, function (sm) { return rd.bytes(sm, false); });
        if (!fmt || fmt.channels > 2) return null;                  // no downmix of a surround track here
        var ecfg = { codec: 'opus', sampleRate: fmt.sampleRate, numberOfChannels: fmt.channels, bitrate: 128000 };
        try { var se = await AudioEncoder.isConfigSupported(ecfg); if (!se || !se.supported) return null; } catch (_) { return null; }
        return { fmt: fmt, ecfg: ecfg, sampleRate: fmt.sampleRate, channels: fmt.channels,
                 cutUs: ed.applied ? Math.max(0, ed.audioResidualUs || 0) : 0 };
    }

    async function muxOpusAudioLazy(demux, rd, muxer, isAborted, plan) {
        var a = demux.audio, WAD = window.WebCodecsAudioDecode;
        var sr = plan.sampleRate, ch = plan.channels, cutUs = plan.cutUs;
        /* The FIRST error is the one that happened. A codec closes itself on
           its own error and queues the callback; the next encode()/decode()
           call throws «Cannot call … on a closed codec» before that callback
           runs — and used to be what the caller saw. */
        var err = null, closedCall = false, first = true, muxed = 0;
        function fail(e) { err = err || e; }
        var aenc = new AudioEncoder({
            output: function (chunk, meta) {
                try { muxer.addAudioChunk(chunk, (first && meta) ? meta : undefined, Math.max(0, chunk.timestamp - cutUs)); muxed++; first = false; }
                catch (e) { fail(e); }
            },
            error: fail
        });
        aenc.configure(plan.ecfg);
        // Everything before cutUs is dropped; the frame that straddles it is cut
        // at the sample, so the first packet starts exactly at the picture.
        function cutHead(d) {
            var n0 = d.numberOfFrames, k = Math.min(n0, Math.max(0, Math.round((cutUs - d.timestamp) * sr / 1e6)));
            var n = n0 - k, chs = d.numberOfChannels, buf = new Float32Array(n * chs), tmp = new Float32Array(n0);
            for (var c = 0; c < chs; c++) { d.copyTo(tmp, { planeIndex: c, format: 'f32-planar' }); buf.set(tmp.subarray(k), c * n); }
            return new AudioData({ format: 'f32-planar', sampleRate: sr, numberOfFrames: n, numberOfChannels: chs, timestamp: cutUs, data: buf });
        }
        var dec = new AudioDecoder({
            output: function (raw) {
                var d = raw;
                try {
                    // The label and the channel count the track was planned for.
                    d = WAD.conformAudioData(raw, sr, ch);
                    var endUs = d.timestamp + d.numberOfFrames * 1e6 / sr;
                    if (endUs <= cutUs) return;                          // wholly before the picture
                    if (d.timestamp < cutUs) {
                        // A cut that falls on the frame's end within rounding leaves
                        // nothing of it (1024 priming samples = exactly one frame).
                        if (d.numberOfFrames - Math.round((cutUs - d.timestamp) * sr / 1e6) <= 0) return;
                        var r = cutHead(d); aenc.encode(r); r.close();
                    }
                    else aenc.encode(d);
                } catch (e) { if (aenc.state === 'closed') closedCall = true; else fail(e); }
                finally {
                    try { raw.close(); } catch (_) {}
                    if (d !== raw) { try { d.close(); } catch (_) {} }
                }
            },
            error: fail
        });
        dec.configure(plan.fmt.config);
        var tick = function () { return new Promise(function (r) { setTimeout(r, 0); }); };
        async function firstError() {
            await tick();                                            // let a queued error callback land
            return err || new Error(closedCall ? 'audio encoder closed' : 'audio codec closed');
        }
        try {
            for (var i = 0; i < a.samples.length; i++) {
                if (isAborted && isAborted()) throw new DOMException('Aborted', 'AbortError');
                if (err) throw err;
                if (closedCall || dec.state === 'closed' || aenc.state === 'closed') throw await firstError();
                var sm = a.samples[i];
                var bytes = sm.data || await rd.bytes(sm, false);
                if (dec.state === 'closed') throw await firstError();
                dec.decode(new EncodedAudioChunk({ type: 'key', timestamp: sm.timestamp, duration: sm.duration || 0, data: bytes }));
                while (dec.decodeQueueSize > 32 || aenc.encodeQueueSize > 32) { await tick(); if (err) break; }
            }
            if (dec.state === 'closed') throw await firstError();
            await dec.flush();
            if (closedCall || aenc.state === 'closed') throw await firstError();
            await aenc.flush();
        } catch (e) {
            if (e && e.name === 'AbortError') throw e;
            throw err || e;
        } finally {
            try { dec.close(); } catch (_) {}
            try { aenc.close(); } catch (_) {}
        }
        if (err) throw err;
        if (!muxed) throw new Error('webm-audio: relay produced no packets');
        return muxed;
    }

    // Streaming transcode: demux → VideoDecoder → VideoEncoder(VP9) →
    // webm-muxer, bounded memory (backpressure caps in-flight frames). Audio
    // decoded to Opus. Mirrors the hardened decode loop + DTS-safe muxing
    // from webcodecs-pipeline.js run().
    async function fromVideoBlob(blob, opts) {
        opts = opts || {};
        if (!blob) throw new Error('webm-encoder: no input');
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};
        var signal = opts.signal;
        var aborted = false;
        if (signal) { if (signal.aborted) throw new DOMException('Aborted', 'AbortError'); signal.addEventListener('abort', function () { aborted = true; }); }
        function throwIfAborted() { if (aborted) throw new DOMException('Aborted', 'AbortError'); }

        await Promise.all([ensureMuxer(), ensureMp4Box()]);
        var file = (blob instanceof File) ? blob : new File([blob], 'in.mp4', { type: blob.type || 'video/mp4' });
        /* Lazy: the sample TABLE now, the bytes in 8 MB windows while feeding.
           The eager demux held every sample of the file on the heap for the
           whole transcode. A file the lazy reader declines (fragmented, moov
           it cannot place) takes the eager path, as before. */
        var WCP = window.WebCodecsPipeline, demux = null;
        try { demux = await WCP._demuxMp4(file, null, { lazy: true }); } catch (_) { demux = null; }
        if (!demux || demux.declined || !demux.lazy) demux = await WCP._demuxMp4(file);
        var rd = (demux && demux.lazy && WCP._lazySampleReader) ? WCP._lazySampleReader(demux) : null;
        throwIfAborted();
        if (!demux || !demux.samples || !demux.samples.length) throw new Error('webm-encoder: demux produced no video samples');

        var inW = demux.width, inH = demux.height;
        var rotation = demux.rotation || 0;
        var fps = demux.fps || 30;
        // Output = coded dims (rotation re-emitted as webm-muxer metadata; no
        // pixel rotation — same policy as the mp4 pipeline).
        var outW = inW, outH = inH;

        var crf = +opts.crf; if (!isFinite(crf)) crf = 24;
        var bitrate = bitrateFor(outW, outH, fps, crf);
        var vc = await pickVp9(outW, outH, bitrate, fps);
        if (!vc) throw new Error('webm-encoder: no VP9 encoder for ' + outW + 'x' + outH);

        var MuxerNs = window.WebMMuxer;
        var videoMuxOpts = { codec: 'V_VP9', width: outW, height: outH, frameRate: fps };
        if (rotation) videoMuxOpts.rotation = rotation;
        var hasAudio = !!(demux.audio && demux.audio.samples && demux.audio.samples.length);
        /* The sound's real format first: the muxer's audio track is written
           from it (planRelay). What the relay cannot carry is decoded whole
           here, before the muxer, for the same reason. */
        var aPlan = null, aWhole = null;
        if (hasAudio) {
            try { aPlan = await planRelay(demux, rd); } catch (_) { aPlan = null; }
            throwIfAborted();
            if (!aPlan) {
                try { aWhole = await decodeWholeAudio(file, function () { return aborted; }); }
                catch (audioErr) {
                    if (audioErr && audioErr.name === 'AbortError') throw audioErr;
                    throw new Error('webm-audio: ' + (audioErr.message || audioErr));
                }
            }
        }
        var aRate = aPlan ? aPlan.sampleRate : (aWhole ? aWhole.sampleRate : 0);
        var aCh = aPlan ? aPlan.channels : (aWhole ? Math.min(2, aWhole.numberOfChannels) : 0);
        /* A big result streams to OPFS instead of piling up as one buffer (and
           a second copy for the Blob): the shared disk lane, which reserves its
           room up front and refuses honestly (null → RAM, as before). The
           _muxtmp_ prefix is the pipeline's, so its sweeper collects a file a
           crashed run leaves behind. */
        var durSec = demux.durationSec || (samples0Dur(demux) / 1e6) || 0;
        var estBytes = ((bitrate + (demux.audio ? 128000 : 0)) / 8) * durSec;
        var lane = null;
        if (estBytes >= 120 * 1048576 && typeof window.__opfsMuxLane === 'function') {
            try { lane = await window.__opfsMuxLane(MuxerNs, { reserveBytes: Math.round(estBytes * 1.15) + 8 * 1048576, prefix: '_muxtmp_', ext: 'webm', lane: 'webm-encoder' }); }
            catch (_) { lane = null; }
        }
        var muxer = new MuxerNs.Muxer({
            target: lane ? lane.target : new MuxerNs.ArrayBufferTarget(),
            video: videoMuxOpts,
            audio: hasAudio ? { codec: 'A_OPUS', numberOfChannels: aCh, sampleRate: aRate } : undefined,
            firstTimestampBehavior: 'offset'
        });

        var encErr = null, muxErr = null;
        var firstChunk = true;
        var encoder = new VideoEncoder({
            output: function (chunk, meta) {
                try {
                    var safe = meta || {};
                    if (firstChunk) { firstChunk = false; }   // VP9 needs no colorSpace/avcC patch (webm-muxer reads none)
                    // webm-muxer takes (chunk, meta, timestamp): WebM has no decode time. The
                    // running-sum «offset» passed here until 04.11.2026 was dropped by it — and
                    // was the same drifting formula that broke the mp4 writers' ctts.
                    muxer.addVideoChunk(chunk, safe, chunk.timestamp);
                } catch (e) { muxErr = muxErr || e; }
            },
            error: function (e) { encErr = encErr || (e instanceof Error ? e : new Error(String(e && e.message || e))); }
        });
        encoder.configure(vc.config || { codec: vc.codec, width: outW, height: outH, bitrate: bitrate, framerate: fps });

        // Decode loop with backpressure (same shape as webcodecs-pipeline run()).
        var decodedFrames = [];
        var decodeError = null;
        var decoder = new VideoDecoder({
            output: function (frame) { if (aborted) { try { frame.close(); } catch (_) {} return; } decodedFrames.push(frame); },
            error: function (e) { decodeError = decodeError || e; }
        });
        var decCfg = { codec: demux.codec, codedWidth: inW, codedHeight: inH, description: demux.avcC || demux.hvcC || demux.av1C || demux.vpcC };
        var decOk = false, decErr2 = null;
        var HINTS = ['prefer-hardware', 'prefer-software', null];
        for (var dh = 0; dh < HINTS.length; dh++) {
            try {
                var c2 = Object.assign({}, decCfg);
                if (HINTS[dh]) c2.hardwareAcceleration = HINTS[dh];
                var probe = await VideoDecoder.isConfigSupported(c2);
                if (!probe || !probe.supported) continue;
                decoder.configure(probe.config || c2); decOk = true; break;
            } catch (e) { decErr2 = e; }
        }
        if (!decOk) throw new Error('webm-encoder: decoder-configure-failed ' + (demux.codec || '') + (decErr2 ? ' | ' + decErr2.message : ''));

        if (hasAudio) {
            var isAb = function () { return aborted; };
            try {
                if (aPlan) await muxOpusAudioLazy(demux, rd, muxer, isAb, aPlan);
                else { await muxOpusAudio(aWhole, muxer, isAb); aWhole = null; }
            } catch (audioErr) {
                if (audioErr && audioErr.name === 'AbortError') throw audioErr;
                throw new Error('webm-audio: ' + (audioErr.message || audioErr));
            }
            // hdr / label next to sr / ch: how often the sample entry and the
            // decoder's label disagree with the stream, measured in the field.
            try { window.__ae && window.__ae('feature_use', { action: 'webm_audio_lane', lane: aPlan ? 'relay' : 'whole', demux: demux.lazy ? 'lazy' : 'eager', opfs: lane ? 1 : 0,
                sr: aRate, ch: aCh, hdr: (demux.audio.sampleRate || 0) + '/' + (demux.audio.channels || 0), label: aPlan ? aPlan.fmt.labelRate : 0 }); } catch (_) {}
        }

        var samples = demux.samples;
        var totalFrames = samples.length;
        var frameCount = 0;
        /* Backpressure on the ENCODER as well as the decoder. A decoded frame
           handed to encode() stays alive until the encoder is done with it,
           and a hardware decoder has a small fixed pool of output frames: with
           no ceiling here the VP9 (software) encoder's queue soaked up the
           whole pool, the decoder stopped emitting without an error, and the
           transcode sat at ~90 % forever — 6 of 6 runs, Edge 153 / NVIDIA,
           2026-09-25 (/video-speed WebM output). The pipeline found the same
           wall for its own VP9 lane: queue 24 → 0 of 6, 8 → 6 of 6
           (reference_vp9_rescue_measured_not_guessed_2026_08_24).
           And a stop must never be silent again: every wait below is abort-
           aware and bounded by STALL_MS of VISIBLE time without a frame moving
           (a hidden tab legitimately pauses media work), so a wedge becomes a
           typed error the caller's fallback can take, not an endless bar. */
        var ENC_Q_MAX = 8, STALL_MS = 20000;
        var visNow = (window.WebCodecsPipeline && window.WebCodecsPipeline.visibleNow) || function () { return performance.now(); };
        var lastMove = visNow(), outFrames = 0;
        var tick = function () { return new Promise(function (r) { setTimeout(r, 0); }); };
        function stallCheck(where) {
            throwIfAborted();
            if (decodeError) throw new Error('decode: ' + (decodeError.message || decodeError));
            if (encErr) throw new Error('vp9-encode: ' + (encErr.message || encErr));
            if (visNow() - lastMove > STALL_MS) {
                var e = new Error('webm-encoder-stall: ' + where + ' fed=' + fed + ' encoded=' + frameCount + '/' + totalFrames +
                    ' decQ=' + decoder.decodeQueueSize + ' encQ=' + encoder.encodeQueueSize + ' held=' + decodedFrames.length);
                e.code = 'stall';
                try { window.__ae && window.__ae('feature_use', { action: 'webm_encoder_stall', where: where, fed: fed, encoded: frameCount, total: totalFrames,
                    decQ: decoder.decodeQueueSize, encQ: encoder.encodeQueueSize, held: decodedFrames.length }); } catch (_) {}
                throw e;
            }
        }
        async function drainOne() {
            if (!decodedFrames.length) return false;
            while (encoder.encodeQueueSize >= ENC_Q_MAX) { await tick(); stallCheck('encode'); }
            var fr = decodedFrames.shift();
            if (!fr) return false;
            if (encErr) { try { fr.close(); } catch (_) {} throw new Error('vp9-encode: ' + (encErr.message || encErr)); }
            if (muxErr) { try { fr.close(); } catch (_) {} throw new Error('webm-mux: ' + (muxErr.message || muxErr)); }
            try { encoder.encode(fr); } finally { try { fr.close(); } catch (_) {} }
            frameCount++;
            lastMove = visNow();
            if (totalFrames > 0) onProgress(Math.min(0.95, frameCount / totalFrames));
            return true;
        }
        // flush() with a watchdog: a wedged codec never settles its flush
        // promise, and an un-raced await there hung the caller for good.
        async function settle(p, where) {
            var done = false, err = null;
            p.then(function () { done = true; }, function (e) { done = true; err = e; });
            while (!done) { await new Promise(function (r) { setTimeout(r, 50); }); stallCheck(where); }
            return err;
        }
        var fed = 0;
        for (var i = 0; i < samples.length; i++) {
            throwIfAborted();
            while (decoder.decodeQueueSize > 30 || decodedFrames.length > 20) {
                await tick();
                while (await drainOne()) {}
                stallCheck('decode');
            }
            var s = samples[i];
            decoder.decode(new EncodedVideoChunk({ type: s.is_sync ? 'key' : 'delta', timestamp: s.timestamp, duration: s.duration, data: s.data || await rd.bytes(s, true) }));
            fed++;
            await tick();
            while (await drainOne()) {}
            if (decodeError) throw new Error('decode: ' + (decodeError.message || decodeError));
        }
        // The decoder's last frames arrive DURING its flush and must keep
        // moving into the encoder, or the flush itself waits on the pool.
        lastMove = visNow();
        var flushDone = false;
        decoder.flush().then(function () { flushDone = true; }, function () { flushDone = true; });
        while (!flushDone) { await tick(); while (await drainOne()) {} stallCheck('decoder-flush'); }
        while (await drainOne()) {}
        try { decoder.close(); } catch (_) {}
        lastMove = visNow();
        await settle(encoder.flush(), 'encoder-flush');
        try { encoder.close(); } catch (_) {}
        if (encErr) throw new Error('vp9-encode: ' + (encErr.message || encErr));
        if (muxErr) throw new Error('webm-mux: ' + (muxErr.message || muxErr));
        if (frameCount === 0) throw new Error('webm-encoder: 0 frames encoded');

        // Audio went in FIRST (above). A silent WebM from a source WITH audio
        // is wrong → any audio failure has already thrown for the caller's
        // FFmpeg fallback.
        muxer.finalize();
        if (lane) {
            var f = await lane.finish();
            if (!f || !f.size) throw new Error('webm-encoder: empty output');
            onProgress(1);
            return new Blob([f], { type: 'video/webm' });   // references the disk file, no copy
        }
        var out = muxer.target.buffer;
        if (!out || !out.byteLength) throw new Error('webm-encoder: empty output');
        onProgress(1);
        return new Blob([out], { type: 'video/webm' });
    }

    window.WebMEncoder = {
        isSupported: isSupported,
        fromVideoBlob: fromVideoBlob,
        _bitrateFor: bitrateFor,
        _pickVp9: pickVp9
    };
})();
